import { useCallback } from 'react'
import { supabase } from '../services/supabase'
import {
  getEfetiveEmpresaId,
  getGlobalEmpresaId,
  setGlobalEmpresaId,
  getGlobalEmpresaPromise,
  setGlobalEmpresaPromise,
  clearEmpresaCache
} from './authCache'

// Re-exportar função de limpar cache para compatibilidade
export { clearEmpresaCache } from './authCache'

/**
 * Hook global para gerenciar empresa ID com cache compartilhado
 * Prioriza a empresa do usuário logado, depois fallback para matriz
 */
export function useEmpresaContext() {
  // Buscar ID da empresa (considera software_house)
  const getEmpresaId = useCallback(async (): Promise<string | null> => {
    // Primeiro, verificar se já temos a empresa efetiva em cache (considera software_house)
    const empresaEfetiva = getEfetiveEmpresaId()
    if (empresaEfetiva) {
      setGlobalEmpresaId(empresaEfetiva)
      return empresaEfetiva
    }

    // Se já temos o ID em cache global, retorna imediatamente
    const cachedEmpresaId = getGlobalEmpresaId()
    if (cachedEmpresaId) {
      return cachedEmpresaId
    }

    // Se já há uma requisição global em andamento, reutiliza a mesma Promise
    const existingPromise = getGlobalEmpresaPromise()
    if (existingPromise) {
      return existingPromise
    }

    // Criar nova requisição global - buscar empresa do usuário logado
    const promise = (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser()

        if (user) {
          const { data: usuario, error: usuarioError } = await supabase
            .from('usuarios')
            .select('empresa_id')
            .eq('auth_user_id', user.id)
            .eq('ativo', true)
            .single()

          if (!usuarioError && usuario?.empresa_id) {
            setGlobalEmpresaId(usuario.empresa_id)
            return usuario.empresa_id
          }
        }

        // Fallback: buscar empresa matriz
        const { data: matriz, error: matrizError } = await supabase
          .from('empresas')
          .select('id')
          .eq('matriz', true)
          .eq('ativo', true)
          .single()

        if (!matrizError && matriz) {
          setGlobalEmpresaId(matriz.id)
          return matriz.id
        }

        // Último fallback: qualquer empresa ativa
        const { data: empresaAtiva, error: ativaError } = await supabase
          .from('empresas')
          .select('id')
          .eq('ativo', true)
          .limit(1)
          .single()

        if (!ativaError && empresaAtiva) {
          setGlobalEmpresaId(empresaAtiva.id)
          return empresaAtiva.id
        }

        console.error('❌ Nenhuma empresa foi encontrada no sistema')
        return null
      } catch (err) {
        console.error('❌ Erro ao buscar empresa:', err)
        return null
      } finally {
        setGlobalEmpresaPromise(null)
      }
    })()

    setGlobalEmpresaPromise(promise)
    return promise
  }, [])

  // Limpar cache (útil para testes ou logout)
  const clearCache = useCallback(() => {
    clearEmpresaCache()
  }, [])

  // Obter ID em cache sem fazer requisição (considera software_house)
  const getCachedEmpresaId = useCallback((): string | null => {
    return getEfetiveEmpresaId() || getGlobalEmpresaId()
  }, [])

  return {
    getEmpresaId,
    clearCache,
    getCachedEmpresaId
  }
}